import { useMutation } from "@apollo/client";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { Button, notification } from "antd";
import { EXPORT } from "queries/centralApi/vats";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { sanitizeHtml } from "utils/sanitizeHtml";

const key = `open${Date.now()}`;

const btn = (
  <Button type="primary" size="small" onClick={() => notification.close(key)}>
    OK
  </Button>
);

export interface DataTable {
  key: number;
  id: ID;
  exportId: ID;
  fileName: string;
  createdAt: string;
  createdBy: string;
  bookEntriesSource: string;
  created: string;
  canBeReexported: boolean;
  autoExported: boolean;
  reexportedAt: boolean;
  size: string;
  uploadedToInterops: boolean;
  totalDocuments: number;
  url: string;
  date: string;
  time: string;
}

export default function useExportApi(refetch?: () => void) {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [bookEntriesType, setBookEntriesType] = useState<string>();
  const { companyId } = useParams();

  const [exportPlatform, { loading }] = useMutation<ExportPlatformOutput, ExportPlatformInput>(
    EXPORT,
    {
      client: clientCentralApiStrawberry,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
    },
  );

  const handleExportPlatform = async (bookEntriesType: string, exportId?: ID) => {
    setBookEntriesType(bookEntriesType);
    setIsLoading(true);

    const response = await exportPlatform({
      variables: {
        company: companyId,
        bookEntriesSource: bookEntriesType,
        exportId: exportId,
      },
    });

    const exportResult = response?.data?.exportPlatform;
    if (!exportResult) {
      setIsLoading(false);
      return;
    }

    if (exportResult.errors?.length > 0) {
      exportResult.errors.forEach(item => {
        item.messages.forEach((message: string) => {
          notification.error({
            message: t("Unexpected error"),
            description: message,
            placement: "bottomLeft",
            duration: 0,
          });
        });
      });
      refetch!();
    }

    if (exportResult.successErrorList && exportResult.successErrorList.length > 0) {
      exportResult.successErrorList.forEach(item => {
        if (item.error) {
          notification.error({
            message: t("Export error"),
            description: (
              <div>
                {item.error}
                <div>{`${t("Error code")}: ${item.errorCode}`}</div>
              </div>
            ),
            placement: "bottomLeft",
            duration: 0,
          });
        }
      });
      refetch!();
    }

    setIsLoading(false);

    if (exportResult.successMessage) {
      notification.open({
        message: t("Export message"),
        description: (
          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(exportResult.successMessage) }} />
        ),
        placement: "bottomLeft",
        duration: 0,
        btn: btn,
        key,
      });
    }

    setUrl(exportResult.downloadUrl);
    refetch!();
  };

  return {
    loading,
    url,
    isLoading,
    handleExportPlatform,
    bookEntriesType,
  };
}
